import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import FileUploaderCard from '../../../../components/FileUploaderCard';
import { readURLAsBinary } from '../../../../shared/utilities';

import { AppState } from '../../../../config/store';

import XLSX from 'xlsx-js-style';

import { useEffect, useRef, useState } from 'react';
import StepGuide from '../../../../components/StepGuide';
import { assembleSurveys, BulkUpdateFeedback } from './bulk-uploader-builder';
import {
  validateWorkbook,
  ValidationResponse,
} from './bulk-uploader-validation';

export default function BulkUploader() {
  const client = useSelector((state: AppState) => state.firestore.data.client);

  const [fileName, setFileName] = useState<string>();

  const [validationErrors, setValidationErrors] =
    useState<ValidationResponse[]>();

  const [progressFeedback, setProgressFeedback] = useState<
    BulkUpdateFeedback[]
  >([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const feedbackEndRef = useRef<HTMLDivElement>(null);
  const feedbackContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Check if user is scrolled to bottom
  const handleScroll = () => {
    if (!feedbackContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      feedbackContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold

    setShouldAutoScroll(isNearBottom);
  };

  // Auto-scroll to latest feedback only if user hasn't scrolled up
  useEffect(() => {
    if (feedbackEndRef.current && isProcessing && shouldAutoScroll) {
      feedbackEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progressFeedback, isProcessing, shouldAutoScroll]);

  const setFileData = async (file: File) => {
    const data = await readURLAsBinary(file);
    setValidationErrors([]);
    setProgressFeedback([]);
    setFileName(file.name);

    const workbook = XLSX.read(data, { type: 'binary' });

    const validationResponses = validateWorkbook(workbook);

    const errors = Object.values(validationResponses).filter(
      (response) => response.success === false,
    );

    console.log('errors', errors);

    if (errors.length) {
      return setValidationErrors(errors);
    }

    setIsProcessing(true);

    assembleSurveys({
      clientId: client.id,
      workbookData: validationResponses,
      feedbackCallback: (feedback: BulkUpdateFeedback) => {
        setProgressFeedback((prev) => [...prev, feedback]);
      },
    }).finally(() => {
      setIsProcessing(false);
    });
  };

  const getTemplateSheet = async () => {
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute(
      'href',
      'https://firebasestorage.googleapis.com/v0/b/asseticom-internal-resources/o/Bulk-Survey-Loading-Template.xlsx?alt=media&token=357377dd-c632-4e9c-b265-004dee12b0ac',
    );
    downloadAnchorNode.setAttribute(
      'download',
      'Bulk-Survey-Loading-Template.xlsx',
    );
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  return (
    <>
      <Box p={2}>
        <Stack alignItems="center">
          <StepGuide
            stepNumber={1}
            text="Download the template file and then add your data."
          />
        </Stack>
        <Box mt={2} display="flex" flexDirection="column" alignItems="center">
          <Button
            onClick={getTemplateSheet}
            variant="contained"
            color="secondary"
          >
            Download Template
          </Button>
        </Box>
        <Box my={4}>
          <StepGuide
            stepNumber={2}
            text="Upload your completed template file."
          />
          <FileUploaderCard
            title="Bulk Upload"
            onChange={(file) => setFileData(file)}
            fileName={fileName}
          />
        </Box>
        {validationErrors?.map((response, index) => (
          <Box key={index} mt={2}>
            <Typography variant="caption" gutterBottom>
              {response.identifier}
            </Typography>
            {response.errors.map((error, index) => (
              <Typography
                key={index}
                variant="caption"
                color="error"
                sx={{ display: 'block' }}
              >
                {error}
              </Typography>
            ))}
          </Box>
        ))}
        {isProcessing && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Processing... Please keep this page open.
            </Typography>
            <LinearProgress />
          </Box>
        )}
        {progressFeedback.length > 0 && (
          <Box
            ref={feedbackContainerRef}
            onScroll={handleScroll}
            mt={2}
            p={2}
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {progressFeedback.map((feedback, index) => (
              <Typography
                key={index}
                variant="caption"
                gutterBottom
                color={feedback.error ? 'error' : 'primary'}
                sx={{ display: 'block' }}
              >
                {feedback.status}...
              </Typography>
            ))}
            <div ref={feedbackEndRef} />
          </Box>
        )}
      </Box>
    </>
  );
}
